import { ref, computed, getCurrentInstance, onMounted } from 'vue';
import { hasPermissionByDescription, verifyPermissionDescription } from '@/components/helpers';
import { PERMISSION_IDS } from '@/constants';
import AuthService from '@/services/auth.service';
import WebStorage from '@/common/WebStorage';

export function useProjectPermission() {
	const instance = getCurrentInstance();
	const proxy = instance?.proxy as any;
	const store = proxy?.$store;
	const router = proxy?.$router;
	const route = proxy?.$route;

	const dataProject = ref<any>('');
	const dataIntegration = ref<any>('');

	const manageProjectPermission = computed(() => 
		hasPermissionByDescription(PERMISSION_IDS?.manageProjectId, dataIntegration.value?.childrens)
	);

	const editProjectPermission = computed(() => 
		hasPermissionByDescription(PERMISSION_IDS?.editProjectId, dataProject.value?.childrens)
	);

	const createProjectPermission = computed(() => 
		hasPermissionByDescription(PERMISSION_IDS?.createProjectId, dataProject.value?.childrens)
	);

	const deleteProjectPermission = computed(() => 
		hasPermissionByDescription(PERMISSION_IDS?.deleteProjectId, dataProject.value?.childrens)
	);

	const duplicateProjectPermission = computed(() => 
		hasPermissionByDescription(PERMISSION_IDS?.duplicateProjectId, dataProject.value?.childrens)
	);

	const shareProjectPermission = computed(() => 
		hasPermissionByDescription(PERMISSION_IDS?.shareProjectId, dataProject.value?.childrens)
	);

	const createFlowsPermission = computed(() => 
		hasPermissionByDescription(PERMISSION_IDS?.createFlowId, dataProject.value?.childrens)
	);

	const workspace = computed(() => store?.getters.workspace);

	const validationAccessProject = async () => {
		const permission = JSON.parse(WebStorage.getItem('userPermission') || '{}');
		dataIntegration.value = verifyPermissionDescription(PERMISSION_IDS?.manageIntegrationId, permission?.access);
		dataProject.value = verifyPermissionDescription(PERMISSION_IDS?.manageProjectId, dataIntegration.value?.childrens);

		if (!dataIntegration.value && !dataProject.value) {
			await AuthService.logout();

			return store?.commit('activeAlert', {
				message: proxy?.$locale.baseText('login.noPermission'),
				status: 'error',
			});
		} else if (!dataProject.value && route?.name === 'projectDetailing') {
			router?.push({ name: 'project' });
		}
	};

	// Inicializar automaticamente como no mixin original
	onMounted(async () => {
		await validationAccessProject();
	});

	const canAcessProject = () => {
		return manageProjectPermission.value && workspace.value?.ativo;
	};

	const canRenameProject = (project: any, projectStatusActive: boolean) => {
		return editProjectPermission.value && projectStatusActive && project.permissao == 'Editar' && workspace.value?.ativo ? true : false;
	};

	const canToggleActiveProject = (project: any) => {
		return editProjectPermission.value && project.permissao == 'Editar' && workspace.value?.ativo;
	};

	const canCreateProject = () => {
		return createProjectPermission.value && workspace.value?.ativo;
	};

	const canDeleteProject = (project: any, projectStatusActive: boolean) => {
		return deleteProjectPermission.value && project.permissao == 'Editar' && workspace.value?.ativo ? true : false;
	};

	const canDuplicateProject = (project: any) => {
		return duplicateProjectPermission.value && workspace.value?.ativo ? true : false;
	};

	const canShareProject = (project: any) => {
		return shareProjectPermission.value && project.permissao == 'Editar' && workspace.value?.ativo ? true : false;
	};

	const canViewShareProject = (project: any) => {
		return shareProjectPermission.value && workspace.value?.ativo ? true : false;
	};

	const canShareBatchProject = () => {
		return shareProjectPermission.value && workspace.value?.ativo;
	};

	const canCreateFlow = (project: any, projectStatusActive: boolean) => {
		return createFlowsPermission.value && projectStatusActive && project.permissao == 'Editar' && workspace.value?.ativo ? true : false;
	};

	return {
		PERMISSION_IDS,
		dataProject,
		dataIntegration,
		manageProjectPermission,
		editProjectPermission,
		createProjectPermission,
		deleteProjectPermission,
		duplicateProjectPermission,
		shareProjectPermission,
		createFlowsPermission,
		workspace,
		validationAccessProject,
		canAcessProject,
		canRenameProject,
		canToggleActiveProject,
		canCreateProject,
		canDeleteProject,
		canDuplicateProject,
		canShareProject,
		canViewShareProject,
		canShareBatchProject,
		canCreateFlow,
	};
}
